import { Injectable, signal, computed, inject } from '@angular/core';
import { BOX_TYPES, BoxType, MenuItem, OrderItem } from '../models/types';
import { ConfigService } from './config.service';

export interface CartItem {
  menuItem: MenuItem;
  quantity: number;
}

@Injectable({
  providedIn: 'root'
})
export class CartService {
  private configService = inject(ConfigService);

  //Constants
  boxTypes = BOX_TYPES;

  // Reactively track cart items
  private cartItemsSignal = signal<CartItem[]>([]);

  // Public read-only signals
  readonly items = computed(() => this.cartItemsSignal());

  readonly itemCount = computed(() => {
    return this.cartItemsSignal().reduce((sum, item) => sum + item.quantity, 0);
  });

  // Calculate subtotal of food items alone
  readonly foodSubtotal = computed(() => {
    return this.cartItemsSignal().reduce((sum, item) => sum + (item.menuItem.price * item.quantity), 0);
  });

  // Calculate box pricing dynamically from ConfigService
  readonly boxesSubtotal = computed(() => {
    return this.cartItemsSignal().reduce((sum, item) => {
      const boxPrice = this.configService.getBoxPrice(item.menuItem.box_type);
      return sum + (boxPrice * item.quantity);
    }, 0);
  });

  // Combined subtotal (Food + Boxes) before delivery fee is added
  readonly subtotal = computed(() => {
    return this.foodSubtotal() + this.boxesSubtotal();
  });

  // Count box counts of each type for invoice breakout
  readonly boxesBreakdown = computed(() => {
    const counts = Object.fromEntries(this.boxTypes.map(box => [box.id, 0])) as Record<BoxType, number>;
    this.cartItemsSignal().forEach(item => {
      if (item.menuItem.box_type in counts) {
        counts[item.menuItem.box_type] += item.quantity;
      }
    });
    return counts;
  });

  constructor() {
    // Load cart from localStorage if available
    const savedCart = localStorage.getItem('dagata_cart');
    if (savedCart) {
      try {
        this.cartItemsSignal.set(JSON.parse(savedCart));
      } catch (e) {
        console.warn('Could not load cart from storage: ', e);
      }
    }
  }

  private saveToLocalStorage(items: CartItem[]) {
    localStorage.setItem('dagata_cart', JSON.stringify(items));
  }

  addItem(menuItem: MenuItem, quantity: number = 1) {
    if (quantity <= 0 || menuItem.enabled === false) return;

    this.cartItemsSignal.update(currentItems => {
      const existingItemIndex = currentItems.findIndex(item => item.menuItem.id === menuItem.id);
      let newItems: CartItem[];

      if (existingItemIndex > -1) {
        newItems = currentItems.map((item, index) =>
          index === existingItemIndex
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      } else {
        newItems = [...currentItems, { menuItem, quantity }];
      }

      this.saveToLocalStorage(newItems);
      return newItems;
    });
  }

  removeItem(menuItemId: string) {
    this.cartItemsSignal.update(currentItems => {
      const newItems = currentItems.filter(item => item.menuItem.id !== menuItemId);
      this.saveToLocalStorage(newItems);
      return newItems;
    });
  }

  updateQuantity(menuItemId: string, quantity: number) {
    if (quantity <= 0) {
      this.removeItem(menuItemId);
      return;
    }

    this.cartItemsSignal.update(currentItems => {
      const newItems = currentItems.map(item =>
        item.menuItem.id === menuItemId ? { ...item, quantity } : item
      );
      this.saveToLocalStorage(newItems);
      return newItems;
    });
  }

  clearCart() {
    this.cartItemsSignal.set([]);
    localStorage.removeItem('dagata_cart');
  }

  setItems(items: CartItem[]) {
    this.cartItemsSignal.set(items);
    this.saveToLocalStorage(items);
  }

  // Convert cart items into OrderItem objects for checkout submission
  getOrderItems(): OrderItem[] {
    return this.cartItemsSignal().map(item => ({
      menu_item_id: item.menuItem.id || '',
      name: item.menuItem.name,
      price: item.menuItem.price,
      box_type: item.menuItem.box_type,
      box_price: this.configService.getBoxPrice(item.menuItem.box_type),
      quantity: item.quantity
    }));
  }
}
